<script setup lang="ts">
import { AnimatePresence } from 'motion-v'
import SeoForm from '@/components/SeoForm.vue'
import SeoReport from '@/components/SeoReport.vue'
import { useSeoStore } from '@/stores/seo'

const store = useSeoStore()

const checks = [
  {
    name: 'Title & Meta Description',
    detail: 'Both present, title in the 30–60 character range, description in the 70–160 range.',
  },
  {
    name: 'Heading Structure',
    detail: 'Exactly one h1 on the homepage, and whether there are h2 sub-headings under it.',
  },
  {
    name: 'robots.txt',
    detail:
      'Whether it exists, what it returns, and whether it blocks all crawlers or Googlebot specifically.',
  },
  {
    name: 'Sitemap Discoverability',
    detail:
      'Whether /sitemap.xml is reachable, how many URLs it lists, and whether robots.txt points to it.',
  },
  {
    name: 'Canonical & Social Tags',
    detail:
      'The canonical tag — including whether it points at a different site — plus OpenGraph and Twitter card tags.',
  },
  {
    name: 'Mobile Viewport & Language',
    detail: 'The viewport meta tag and the lang attribute on the html element.',
  },
  {
    name: 'Image Alt Text',
    detail: 'What share of img tags on the homepage carry alt text.',
  },
  {
    name: 'Local Business Signals',
    detail:
      'LocalBusiness JSON-LD schema, a tappable tel: link, and a visible phone number and ZIP.',
  },
]
</script>

<template>
  <section class="page-header">
    <p class="section-marker">SEO audit</p>
    <h1 class="page-title display">Can people find you on Google?</h1>
    <p class="page-lede">
      I run eight checks against your site: title and meta description, heading structure,
      robots.txt, whether your sitemap is discoverable, canonical and social tags, mobile viewport
      and language, image alt text, and the local-business markup that decides whether your number
      shows up when somebody nearby searches. A search engine that can't tell what you do or where
      you're located sends that customer to whoever's listing is easier to read. You get a score and
      what to fix first, on screen, in about 15 seconds.
    </p>
  </section>

  <section class="audit-card">
    <SeoForm />
    <AnimatePresence>
      <SeoReport
        v-if="store.status === 'success' && store.report"
        :key="store.report.host + store.report.timestamp"
        :report="store.report"
        :audit-id="store.auditId"
      />
    </AnimatePresence>
  </section>

  <section class="checks-section" aria-labelledby="checks-heading">
    <header class="checks-header">
      <h2 id="checks-heading">What I check</h2>
      <p class="checks-caption">Eight checks, run against every submission.</p>
    </header>
    <div class="check-table-wrap">
      <table class="check-table">
        <thead>
          <tr>
            <th scope="col">Check</th>
            <th scope="col">What it looks at</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in checks" :key="c.name">
            <th scope="row">{{ c.name }}</th>
            <td>{{ c.detail }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<style scoped>
.page-header {
  max-width: 40em;
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
  margin: 2rem 0 2.25rem;
}

.page-title {
  margin: 0;
  font-size: clamp(1.9rem, 4vw, 2.7rem);
  color: var(--text);
}

.page-lede {
  margin: 0;
  font-size: 1.05rem;
  line-height: 1.6;
  color: var(--text-muted);
  font-variation-settings: 'opsz' 18;
}

.audit-card {
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--shadow-sm);
  padding: 2rem;
  margin: 2rem 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.checks-section {
  margin: 3rem 0 2.5rem;
}

.checks-header {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1.25rem;
}

.checks-header h2 {
  margin: 0;
  font-family: var(--font-display);
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--text);
  font-variation-settings: 'opsz' 36;
}

.checks-caption {
  margin: 0;
  font-family: var(--font-mono);
  font-size: 0.78rem;
  letter-spacing: 0.04em;
  color: var(--text-faint);
}

.check-table-wrap {
  border: 1px solid var(--border);
  border-radius: var(--radius);
  overflow: hidden;
  overflow-x: auto;
}

.check-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.92rem;
}

.check-table th,
.check-table td {
  text-align: left;
  padding: 0.85rem 1.1rem;
  vertical-align: top;
}

.check-table thead th {
  background: var(--surface-2);
  font-family: var(--font-mono);
  font-size: 0.68rem;
  font-weight: 500;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--text-muted);
  border-bottom: 1px solid var(--border);
}

.check-table tbody th {
  width: 15rem;
  font-family: var(--font-display);
  font-weight: 500;
  font-size: 0.98rem;
  color: var(--text);
  font-variation-settings: 'opsz' 20;
}

.check-table tbody td {
  color: var(--text-muted);
  line-height: 1.55;
}

.check-table tbody tr:not(:last-child) th,
.check-table tbody tr:not(:last-child) td {
  border-bottom: 1px solid var(--border);
}

@media (max-width: 600px) {
  .audit-card {
    padding: 1.25rem;
  }
  .check-table tbody th {
    width: 9rem;
  }
  .check-table th,
  .check-table td {
    padding: 0.7rem 0.85rem;
    font-size: 0.86rem;
  }
}
</style>
